import { Elements } from "@stripe/react-stripe-js";
import clsx from "clsx";
import { useRouter } from "next/compat/router";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useRef, useState } from "react";

import BecomeAMemberCheckoutPaymentForm from "./BecomeAMemberCheckoutPaymentForm";
import BecomeAMemberCheckoutPayPalButton from "./BecomeAMemberCheckoutPayPalButton";
import BecomeAMemberCheckoutSummary from "./BecomeAMemberCheckoutSummary";
import BecomeAMemberExistingCaution from "./BecomeAMemberExistingCaution";
import BecomeAMemberEndTrial from "./BecomeMemberEndTrial";
import LayoutCard from "~/components/LayoutCard";
import Skeleton from "~/components/Skeleton";
import StripeJsLoadError from "~/components/StripeJsLoadError";
import {
	useStripePaymentMethods,
	useStripeProration,
	useStripeSubscription,
} from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import useJoiningPrice from "~/hooks/useJoiningPrice";
import usePayPalSubscription from "~/hooks/usePayPalSubscription";
import usePlanConfig from "~/hooks/usePlanConfig";
import useSale from "~/hooks/useSale";
import useStripeJsLoad from "~/hooks/useStripeJsLoad";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";
import { omitCheckoutQuery } from "~/lib/membership-checkout-query";
import { getApplicableJoiningFee } from "~/lib/membership-joining-price";
import {
	canUsePayPalCheckout,
	shouldLockPlanSelection,
} from "~/lib/stripe/checkoutPaymentGuards";
import getStripeElementsAppearance from "~/lib/stripe/getStripeElementsAppearance";
import { resolveCheckoutElementsAmount } from "~/lib/stripe/resolveCheckoutElementsAmount";
import { shouldShowCheckoutSkeleton } from "~/lib/stripe/shouldShowCheckoutSkeleton";
import { getStripeJsLoadErrorMessage } from "~/lib/stripe/stripeJsLoad";

interface BecomeAMemberCheckoutProps {
	isModal?: boolean;
}

const BecomeAMemberCheckout = ({ isModal }: BecomeAMemberCheckoutProps) => {
	const router = useRouter();
	const { planConfig, isLoadingPlanConfig } = usePlanConfig();

	const {
		flags: { isChangingPlan },
		clearCheckout,
		isPaymentSubmitting,
	} = useCheckout();
	const isPlanLocked = shouldLockPlanSelection(isPaymentSubmitting);

	const { subscription, isActive: isStripeActive } = useStripeSubscription();
	const { sale } = useSale();
	const { offer } = useJoiningPrice();
	const joiningFee = isChangingPlan
		? 0
		: getApplicableJoiningFee(planConfig, offer);

	const { proration, isLoadingProration } = useStripeProration(
		isChangingPlan && !isLoadingPlanConfig ? planConfig : undefined,
	);

	const { isLoadingPaymentMethods } = useStripePaymentMethods(
		planConfig.planType,
	);

	const { isLoadingPayPalSubscription } = usePayPalSubscription();

	const { resolvedTheme } = useTheme();

	const [hasMountedCheckout, setHasMountedCheckout] = useState(false);

	const {
		stripePromise,
		isError: isStripeJsError,
		retry: retryStripeJs,
	} = useStripeJsLoad({
		onBlocked: (code) => {
			captureBecomeAMemberEvent("checkout_blocked", {
				surface: isModal ? "modal" : "page",
				interval: planConfig.price?.recurring?.interval,
				promo_code: planConfig.promoCode?.code,
				price_id: planConfig.price?.id,
				error_code: code,
				error_message: getStripeJsLoadErrorMessage({}),
			});
		},
	});

	const appearance = useMemo(
		() => getStripeElementsAppearance(resolvedTheme),
		[resolvedTheme],
	);

	const lastAmountInCents = useRef<number | undefined>(undefined);
	const totalAmountInCents = resolveCheckoutElementsAmount({
		planConfig,
		isLoadingPlanConfig,
		isChangingPlan: !!isChangingPlan,
		proration,
		previousAmount: lastAmountInCents.current,
		joiningFee,
	});

	useEffect(() => {
		lastAmountInCents.current = totalAmountInCents;
	}, [totalAmountInCents]);

	const isTrial = subscription?.status === "trialing";

	const isLoading =
		isLoadingPlanConfig ||
		isLoadingProration ||
		isLoadingPaymentMethods ||
		isLoadingPayPalSubscription;

	useEffect(() => {
		if (!isLoading) {
			setHasMountedCheckout(true);
		}
	}, [isLoading]);

	const showSkeleton = shouldShowCheckoutSkeleton({
		hasMountedCheckout,
		isLoading,
	});

	const elementsOptions = useMemo(
		() => ({
			appearance,
			mode: "subscription" as const,
			currency: planConfig?.currency,
			amount: totalAmountInCents,
		}),
		[appearance, planConfig?.currency, totalAmountInCents],
	);

	const canUsePayPal = canUsePayPalCheckout({
		paypalPlanId: planConfig.price?.paypalPlanId,
		paypalPlanAmount: planConfig.price?.paypalPlanAmount,
		priceAmount: planConfig.price?.unit_amount,
		hasSale: !!sale,
		hasPromoCode: !!planConfig.promoCode,
		hasTrial: !!planConfig.trialLength,
		isStripeActive: !!isStripeActive,
		hasJoiningFee: joiningFee > 0,
	});

	const paymentContent = isStripeJsError ? (
		<div className="flex flex-col gap-4">
			{canUsePayPal && <BecomeAMemberCheckoutPayPalButton />}
			<StripeJsLoadError canUsePayPal={canUsePayPal} onRetry={retryStripeJs} />
		</div>
	) : (
		<Elements stripe={stripePromise} options={elementsOptions}>
			<BecomeAMemberCheckoutPaymentForm />
		</Elements>
	);

	if (isModal)
		return showSkeleton ? (
			<div className="flex w-full flex-col gap-5">
				<Skeleton className="h-11 w-full" />
				<Skeleton className="h-11 w-full" />
				<Skeleton className="h-11 w-full" />
				<Skeleton className="h-11 w-full" />
			</div>
		) : !isTrial ? (
			<div className="flex flex-col gap-4">
				<BecomeAMemberCheckoutSummary isModal={isModal} />
				{paymentContent}
			</div>
		) : (
			<BecomeAMemberEndTrial />
		);

	return (
		<LayoutCard
			isLoading={showSkeleton}
			hasEnterExitAnimations={isModal}
			title={isTrial ? "Activate Membership" : "Checkout"}
			subtitle={
				isTrial
					? `Become a member to access all benefits.`
					: "Review your plan & payment details"
			}
			button={
				!isTrial
					? {
							label: "Back to Choosing Plans",
							handleClick: isPlanLocked
								? undefined
								: () => {
										if (!isModal) {
											router?.replace(
												{
													pathname: "/become-a-member",
													query: omitCheckoutQuery(router.query),
												},
												undefined,
												{ shallow: true },
											);
											return;
										}
										clearCheckout();
									},
						}
					: undefined
			}
		>
			<div className={clsx("flex w-full flex-col gap-6")}>
				<BecomeAMemberCheckoutSummary />

				<BecomeAMemberExistingCaution />

				{!isTrial ? paymentContent : <BecomeAMemberEndTrial />}
			</div>
		</LayoutCard>
	);
};

export default BecomeAMemberCheckout;
